/**
 * Run now, Pause and Resume — asked first, because Run now starts a real scrape
 * and Pause stops data coming in during an event.
 */

import { useState } from "react";
import { ExternalLink, MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Banner, Button, MenuItem } from "@/components/kit";
import { Floating } from "@/components/kit/Floating";
import {
  runNow,
  scoutJobUrl,
  updateIntegration,
  type Integration,
} from "@/lib/integrations/api";
import { errorText } from "@/spa/useApi";

export type Pending = { code: string; name: string; kind: "run" | "pause" | "resume" };

const ASK: Record<Pending["kind"], (name: string) => string> = {
  run: (name) => `Ask Scout to run ${name} now? This starts a real scrape. When it passes, the doorbell brings the rows in.`,
  pause: (name) => `Pause ${name}? omnium stops reading its Scout runs until you resume it. Scout keeps its own schedule.`,
  resume: (name) => `Resume ${name}? omnium reads its Scout runs again, starting with the next one.`,
};

const DONE: Record<Pending["kind"], string> = {
  run: "Scout was asked to run it. The run shows under Runs when it passes.",
  pause: "Paused. Nothing new comes in until it is resumed.",
  resume: "Resumed. The next passed run comes in.",
};

const ACTION: Record<Pending["kind"], string> = { run: "Run now", pause: "Pause", resume: "Resume" };

/** The question before an action, and what happened after. */
export function useIntegrationActions(onDone: () => void) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: "good" | "danger"; text: string } | null>(null);

  const confirm = () => {
    if (!pending) return;
    setBusy(true);
    const work =
      pending.kind === "run"
        ? runNow(pending.code)
        : updateIntegration(pending.code, { state: pending.kind === "pause" ? "paused" : "on" });
    work
      .then(
        () => {
          setSaid({ tone: "good", text: DONE[pending.kind] });
          onDone();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not do that") }),
      )
      .finally(() => {
        setBusy(false);
        setPending(null);
      });
  };

  const banner = (
    <>
      {pending && (
        <Banner
          tone="warning"
          actions={
            <>
              <Button tone="primary" onClick={confirm} disabled={busy}>
                {busy ? "Working…" : ACTION[pending.kind]}
              </Button>
              <Button tone="subtle" onClick={() => setPending(null)} disabled={busy}>
                Cancel
              </Button>
            </>
          }
        >
          {ASK[pending.kind](pending.name)}
        </Banner>
      )}
      {said && !pending && (
        <Banner tone={said.tone} actions={<Button tone="subtle" onClick={() => setSaid(null)}>Close</Button>}>
          {said.text}
        </Banner>
      )}
    </>
  );
  return { ask: (next: Pending) => { setSaid(null); setPending(next); }, banner };
}

/** The ⋯ menu on a row: Run now · Pause / Resume · Open in Scout. */
export function RowMenu({
  item,
  canWrite,
  onAsk,
}: {
  item: Pick<Integration, "code" | "name" | "state" | "scoutJobId">;
  canWrite: boolean;
  onAsk: (pending: Pending) => void;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const close = () => setOpen(false);
  return (
    <Floating
      open={open}
      onClose={close}
      align="right"
      trigger={
        <Button tone="subtle" icon aria-label={`More for ${item.name}`} onClick={() => setOpen(!open)}>
          <MoreHorizontal size={16} />
        </Button>
      }
    >
      {item.state === "setting_up" ? (
        <MenuItem
          label="Continue setting up"
          onClick={() => {
            close();
            navigate(`/integrations/new?code=${encodeURIComponent(item.code)}`);
          }}
        />
      ) : (
        <MenuItem
          label="Run now"
          disabled={!canWrite || item.state === "off" || !item.scoutJobId}
          onClick={() => {
            close();
            onAsk({ code: item.code, name: item.name, kind: "run" });
          }}
        />
      )}
      {item.state === "on" && (
        <MenuItem
          label="Pause"
          disabled={!canWrite}
          onClick={() => {
            close();
            onAsk({ code: item.code, name: item.name, kind: "pause" });
          }}
        />
      )}
      {(item.state === "paused" || item.state === "paused_by_guard" || item.state === "off") && (
        <MenuItem
          label="Resume"
          disabled={!canWrite}
          onClick={() => {
            close();
            onAsk({ code: item.code, name: item.name, kind: "resume" });
          }}
        />
      )}
      <MenuItem
        label="Open in Scout"
        kind="new tab"
        disabled={!item.scoutJobId}
        onClick={() => {
          close();
          if (item.scoutJobId) window.open(scoutJobUrl(item.scoutJobId), "_blank", "noreferrer");
        }}
      />
    </Floating>
  );
}

export function OpenInScout({ jobId }: { jobId: string | null }) {
  if (!jobId) return null;
  return (
    <a
      href={scoutJobUrl(jobId)}
      target="_blank"
      rel="noreferrer"
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--t-md)" }}
    >
      Open in Scout <ExternalLink size={13} aria-hidden />
    </a>
  );
}
